import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Tabs,
  Table,
  TextInput,
  Select,
  Button,
  Group,
  Badge,
  Pagination,
  Card,
  Stack,
  Text,
  Loader,
  Alert,
  ActionIcon,
  Modal,
  Title,
  Textarea,
  NumberInput,
  Image,
  Grid,
  Collapse,
  ScrollArea,
  Box,
  Tooltip,
  ThemeIcon
} from '@mantine/core';
import {
  IconSearch,
  IconEye,
  IconCheck,
  IconX,
  IconExclamationCircle,
  IconRefresh,
  IconBuildingStore,
  IconPhoto,
  IconChevronDown,
  IconChevronUp,
  IconShieldCheck,
  IconShieldX,
  IconClock,
  IconZoomIn,
  IconDownload,
  IconAlertCircle,
  IconInfoCircle
} from '@tabler/icons-react';
import { useApiClient, useLiveQuery } from '../../api';
import { fetchProtectedBlobUrl } from '../../api/uploads';

const PAGE_LIMIT = 20;

const FILE_KINDS = [
  { kind: 'selfie', label: 'Selfie with ID' },
  { kind: 'front', label: 'Front of ID' },
  { kind: 'back', label: 'Back of ID' },
];

// Fetch the verification belonging to a seller profile via the server-side
// filter. Returns null when absent (unknown id yields an empty list).
async function findVerificationForSeller(api, sellerProfileId) {
  const res = await api.get('/api/v1/admin/verifications', {
    seller_profile_id: sellerProfileId,
  });
  return (res.items || [])[0] ?? null;
}

const SellerVerification = () => {
  const api = useApiClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [actionError, setActionError] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [sellerModalOpen, setSellerModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [commissionRate, setCommissionRate] = useState(5.0);
  const [idVerification, setIdVerification] = useState(null);
  const [idVerificationLoading, setIdVerificationLoading] = useState(false);
  const [idVerificationError, setIdVerificationError] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    frontendData: false,
    serverData: false
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  // ID-document images are auth-gated binaries — fetched as blobs and shown
  // via object URLs, revoked when the modal closes or the component unmounts.
  const [fileUrls, setFileUrls] = useState({});
  const fileUrlsRef = useRef({});

  const revokeFileUrls = useCallback(() => {
    Object.values(fileUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    fileUrlsRef.current = {};
    setFileUrls({});
  }, []);

  useEffect(() => revokeFileUrls, [revokeFileUrls]);

  // Live queue: new registrations and verification submissions/reviews
  // refresh the list as they happen.
  const { data, loading, error, refetch } = useLiveQuery('/api/v1/admin/sellers', {
    page: currentPage,
    limit: PAGE_LIMIT,
    status: statusFilter === 'all' ? undefined : statusFilter,
    search: submittedSearch || undefined,
  }, {
    topics: ['admin.sellers', 'admin.verifications'],
  });

  const sellers = data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / (data?.limit ?? PAGE_LIMIT)));

  const handleSearch = () => {
    setCurrentPage(1);
    setSubmittedSearch(searchTerm);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const loadVerificationFiles = useCallback(async (verification) => {
    revokeFileUrls();
    if (!verification) return;
    const urls = {};
    await Promise.all(
      FILE_KINDS.filter(({ kind }) => verification.files?.[kind]).map(async ({ kind }) => {
        try {
          urls[kind] = await fetchProtectedBlobUrl(
            api,
            `/api/v1/admin/verifications/${verification.id}/files/${kind}`,
          );
        } catch (err) {
          console.error(`Failed to load ${kind} image:`, err);
        }
      })
    );
    fileUrlsRef.current = urls;
    setFileUrls(urls);
  }, [api, revokeFileUrls]);

  const fetchSellerIdVerification = useCallback(async (sellerProfileId) => {
    try {
      setIdVerificationLoading(true);
      setIdVerificationError(null);

      const verification = await findVerificationForSeller(api, sellerProfileId);
      setIdVerification(verification);
      if (verification) {
        await loadVerificationFiles(verification);
      } else {
        setIdVerificationError('No ID verification submitted yet');
      }
    } catch (err) {
      console.error('Error fetching ID verification:', err);
      setIdVerificationError(err.message || 'Failed to load ID verification');
      setIdVerification(null);
    } finally {
      setIdVerificationLoading(false);
    }
  }, [api, loadVerificationFiles]);

  const viewSellerDetails = async (seller) => {
    setSelectedSeller(seller);
    setVerificationNotes(seller.verification_notes || '');
    setCommissionRate(seller.commission_rate ?? 5.0);
    setRejectionReason('');
    setSellerModalOpen(true);

    await fetchSellerIdVerification(seller.id);
  };

  const closeSellerModal = () => {
    setSellerModalOpen(false);
    setIdVerification(null);
    setIdVerificationError(null);
    setRejectionReason('');
    setExpandedSections({ frontendData: false, serverData: false });
    revokeFileUrls();
  };

  const updateIdVerificationStatus = async (status) => {
    if (!idVerification) return;

    const needsReason = status === 'rejected' || status === 'resubmission_required';
    if (needsReason && !rejectionReason.trim()) {
      setIdVerificationError('A rejection reason is required for this action.');
      return;
    }

    try {
      setUpdating(true);
      setIdVerificationError(null);
      const body = { status };
      if (needsReason) body.rejection_reason = rejectionReason.trim();
      if (verificationNotes.trim()) body.admin_notes = verificationNotes.trim();

      const updated = await api.patch(`/api/v1/admin/verifications/${idVerification.id}`, body);
      setIdVerification(updated);

      // Approval also flips is_verified on the linked SellerProfile
      refetch();
    } catch (err) {
      console.error('Error updating ID verification:', err);
      setIdVerificationError(err.message || 'Failed to update ID verification');
    } finally {
      setUpdating(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const openImageModal = (imageUrl, title) => {
    setSelectedImage({ url: imageUrl, title });
    setImageModalOpen(true);
  };

  const updateSellerStatus = async (sellerId, newStatus, isVerified = null) => {
    try {
      setUpdating(true);
      setActionError(null);
      const updateData = {
        seller_status: newStatus,
        verification_notes: verificationNotes || null
      };

      if (isVerified !== null) {
        updateData.is_verified = isVerified;
      }

      if (commissionRate !== selectedSeller?.commission_rate) {
        updateData.commission_rate = commissionRate;
      }

      const updated = await api.patch(`/api/v1/admin/sellers/${sellerId}`, updateData);

      if (selectedSeller?.id === sellerId) {
        setSelectedSeller(updated);
      }

      refetch();
      closeSellerModal();
    } catch (err) {
      console.error('Error updating seller:', err);
      setActionError(err.message || 'Failed to update seller');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'approved': return 'green';
      case 'pending': return 'yellow';
      case 'rejected': return 'red';
      case 'suspended': return 'orange';
      default: return 'gray';
    }
  };

  const getIdVerificationStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'green';
      case 'pending': return 'yellow';
      case 'under_review': return 'pink';
      case 'rejected': return 'red';
      case 'resubmission_required': return 'orange';
      default: return 'gray';
    }
  };

  const getIdVerificationIcon = (status) => {
    switch (status) {
      case 'approved': return IconShieldCheck;
      case 'pending': return IconClock;
      case 'under_review': return IconEye;
      case 'rejected': return IconShieldX;
      case 'resubmission_required': return IconAlertCircle;
      default: return IconInfoCircle;
    }
  };

  const formatBarcodeData = (barcode) => {
    if (!barcode || typeof barcode !== 'object') return null;

    return Object.entries(barcode)
      .filter(([key, value]) => key !== 'id' && value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({
        key: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: String(value)
      }));
  };

  const compareDataValues = (frontendValue, serverValue) => {
    if (frontendValue === serverValue) return 'match';
    if (!frontendValue || !serverValue) return 'missing';
    return 'mismatch';
  };

  const displayError = actionError || (error ? error.message : null);

  if (loading && sellers.length === 0) {
    return (
      <Tabs.Panel value="sellers" pt="xs">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Tabs.Panel>
    );
  }

  return (
    <Tabs.Panel value="sellers" pt="xs">
      <Card shadow="sm" padding="lg" radius="md" withBorder mb="md">
        <Stack gap="md">
          <Group align="center" gap="md">
            <TextInput
              placeholder="Search sellers..."
              leftSection={<IconSearch size="0.9rem" />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              style={{ flex: 1 }}
            />
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              data={[
                { value: 'all', label: 'All Statuses' },
                { value: 'pending', label: 'Pending' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' },
                { value: 'suspended', label: 'Suspended' }
              ]}
            />
            <Button onClick={handleSearch} loading={loading}>
              Search
            </Button>
            <ActionIcon onClick={refetch} variant="light">
              <IconRefresh size="1rem" />
            </ActionIcon>
          </Group>

          {displayError && (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
              {displayError}
            </Alert>
          )}
        </Stack>
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <ScrollArea>
          <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Business Name</Table.Th>
              <Table.Th>Owner</Table.Th>
              <Table.Th>Business Type</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Verified</Table.Th>
              <Table.Th>Applied</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sellers.map((seller) => (
              <Table.Tr key={seller.id}>
                <Table.Td>
                  <Group gap="xs">
                    <IconBuildingStore size="1rem" />
                    <Text fw={500}>{seller.business_name}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    <Text size="sm" fw={500}>{seller.display_name}</Text>
                    <Text size="xs" c="dimmed">{seller.email || seller.business_email}</Text>
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Text tt="capitalize">
                    {(seller.business_type || '').replace('_', ' ')}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={getStatusBadgeColor(seller.seller_status)} variant="light">
                    {seller.seller_status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge color={seller.is_verified ? 'green' : 'gray'} variant="light" size="sm">
                    {seller.is_verified ? 'Yes' : 'No'}
                  </Badge>
                </Table.Td>
                <Table.Td>{formatDate(seller.created_at)}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon
                      variant="light"
                      onClick={() => viewSellerDetails(seller)}
                    >
                      <IconEye size="1rem" />
                    </ActionIcon>
                    {seller.seller_status === 'pending' && (
                      <>
                        <ActionIcon
                          variant="light"
                          color="green"
                          onClick={() => updateSellerStatus(seller.id, 'approved', true)}
                        >
                          <IconCheck size="1rem" />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="red"
                          onClick={() => updateSellerStatus(seller.id, 'rejected', false)}
                        >
                          <IconX size="1rem" />
                        </ActionIcon>
                      </>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        </ScrollArea>

        {sellers.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Text c="dimmed">No sellers found</Text>
          </div>
        )}

        {totalPages > 1 && (
          <Group align="center" mt="md">
            <Pagination
              value={currentPage}
              onChange={setCurrentPage}
              total={totalPages}
            />
          </Group>
        )}
      </Card>

      {/* Seller Details Modal */}
      <Modal
        opened={sellerModalOpen}
        onClose={closeSellerModal}
        title={<Title order={3}>Seller Verification</Title>}
        size="lg"
      >
        {selectedSeller && (
          <Stack gap="md">
            {/* Business Information */}
            <Card withBorder p="md">
              <Title order={4} mb="md">Business Information</Title>
              <Stack gap="sm">
                <Group align="apart">
                  <Text fw={500}>Business Name:</Text>
                  <Text>{selectedSeller.business_name}</Text>
                </Group>
                <Group align="apart">
                  <Text fw={500}>Business Type:</Text>
                  <Text tt="capitalize">
                    {(selectedSeller.business_type || '').replace('_', ' ')}
                  </Text>
                </Group>
                <Group align="apart">
                  <Text fw={500}>Business Email:</Text>
                  <Text>{selectedSeller.business_email}</Text>
                </Group>
                <Group align="apart">
                  <Text fw={500}>Business Phone:</Text>
                  <Text>{selectedSeller.business_phone}</Text>
                </Group>
                {selectedSeller.business_description && (
                  <div>
                    <Text fw={500} mb="xs">Description:</Text>
                    <Text size="sm">{selectedSeller.business_description}</Text>
                  </div>
                )}
              </Stack>
            </Card>

            {/* Address Information */}
            {(selectedSeller.business_address_line1 || selectedSeller.business_city) && (
              <Card withBorder p="md">
                <Title order={4} mb="md">Business Address</Title>
                <Text size="sm">
                  {selectedSeller.business_address_line1}
                  {selectedSeller.business_address_line2 && (
                    <><br />{selectedSeller.business_address_line2}</>
                  )}
                  {selectedSeller.business_address_line1 && <br />}
                  {[selectedSeller.business_city, selectedSeller.business_state].filter(Boolean).join(', ')} {selectedSeller.business_postal_code}
                  <br />
                  {selectedSeller.business_country}
                </Text>
              </Card>
            )}

            {/* Legal Information */}
            {(selectedSeller.tax_id || selectedSeller.business_license) && (
              <Card withBorder p="md">
                <Title order={4} mb="md">Legal Information</Title>
                <Stack gap="sm">
                  {selectedSeller.tax_id && (
                    <Group align="apart">
                      <Text fw={500}>Tax ID:</Text>
                      <Text>{selectedSeller.tax_id}</Text>
                    </Group>
                  )}
                  {selectedSeller.business_license && (
                    <Group align="apart">
                      <Text fw={500}>Business License:</Text>
                      <Text>{selectedSeller.business_license}</Text>
                    </Group>
                  )}
                </Stack>
              </Card>
            )}

            {/* ID Verification Section */}
            <Card withBorder p="md">
              <Group align="center" gap="md" mb="md">
                <Title order={4}>ID Verification</Title>
                {idVerificationLoading && <Loader size="sm" />}
              </Group>

              {idVerificationError && (
                <Alert icon={<IconExclamationCircle size="1rem" />} color="red" mb="md">
                  {idVerificationError}
                </Alert>
              )}

              {!idVerificationLoading && idVerification && (
                <Stack gap="md">
                  {/* Verification Status Overview */}
                  <Card withBorder p="sm" bg="gray.1">
                    <Group align="center" gap="md">
                      <ThemeIcon
                        color={getIdVerificationStatusColor(idVerification.status)}
                        variant="light"
                        size="lg"
                      >
                        {(() => {
                          const IconComponent = getIdVerificationIcon(idVerification.status);
                          return <IconComponent size="1.2rem" />;
                        })()}
                      </ThemeIcon>
                      <div style={{ flex: 1 }}>
                        <Text fw={600} size="sm">
                          Status: {idVerification.status?.replace(/_/g, ' ').toUpperCase() || 'Unknown'}
                        </Text>
                        {idVerification.submitted_at && (
                          <Text size="xs" c="dimmed">
                            Submitted: {formatDate(idVerification.submitted_at)}
                          </Text>
                        )}
                        {idVerification.reviewed_at && (
                          <Text size="xs" c="dimmed">
                            Reviewed: {formatDate(idVerification.reviewed_at)}
                          </Text>
                        )}
                        {idVerification.approved_at && (
                          <Text size="xs" c="dimmed">
                            Approved: {formatDate(idVerification.approved_at)}
                          </Text>
                        )}
                      </div>
                      {idVerification.validation_score != null && (
                        <Stack gap={0} align="center">
                          <Text size="lg" fw={700} c={idVerification.validation_score >= 0.8 ? 'green' : idVerification.validation_score >= 0.6 ? 'orange' : 'red'}>
                            {Math.round(idVerification.validation_score * 100)}%
                          </Text>
                          <Text size="xs" c="dimmed">Confidence</Text>
                        </Stack>
                      )}
                    </Group>
                  </Card>

                  {/* ID Images Display */}
                  {Object.keys(fileUrls).length > 0 && (
                    <Box>
                      <Group align="center" gap="sm" mb="sm">
                        <IconPhoto size="1.2rem" />
                        <Text fw={500}>ID Documents</Text>
                      </Group>
                      <Grid>
                        {FILE_KINDS.filter(({ kind }) => fileUrls[kind]).map(({ kind, label }) => (
                          <Grid.Col span={4} key={kind}>
                            <Card withBorder p="xs">
                              <Stack gap="xs" align="center">
                                <Image
                                  src={fileUrls[kind]}
                                  alt={label}
                                  height={120}
                                  fit="contain"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => openImageModal(fileUrls[kind], label)}
                                />
                                <Text size="xs" ta="center" fw={500}>{label}</Text>
                                <Group gap="xs" justify="center">
                                  <Tooltip label="View full size">
                                    <ActionIcon
                                      size="sm"
                                      variant="light"
                                      onClick={() => openImageModal(fileUrls[kind], label)}
                                    >
                                      <IconZoomIn size="0.8rem" />
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              </Stack>
                            </Card>
                          </Grid.Col>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Barcode Data Display */}
                  {(idVerification.frontend_barcode_data || idVerification.server_barcode_data) && (
                    <Box>
                      <Text fw={500} mb="sm">Barcode Data Analysis</Text>

                      {/* Frontend Barcode Data */}
                      {idVerification.frontend_barcode_data && (
                        <Card withBorder p="sm" mb="sm">
                          <Group
                            align="center"
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggleSection('frontendData')}
                          >
                            <Text fw={500} size="sm" style={{ flex: 1 }}>
                              Frontend Parsed Data
                            </Text>
                            {expandedSections.frontendData ?
                              <IconChevronUp size="1rem" /> :
                              <IconChevronDown size="1rem" />
                            }
                          </Group>
                          <Collapse in={expandedSections.frontendData}>
                            <Stack gap="xs" mt="sm">
                              {formatBarcodeData(idVerification.frontend_barcode_data)?.map(({ key, value }) => (
                                <Group key={key} align="apart">
                                  <Text size="sm" fw={500}>{key}:</Text>
                                  <Text size="sm" style={{ maxWidth: '60%' }} truncate>{value}</Text>
                                </Group>
                              ))}
                            </Stack>
                          </Collapse>
                        </Card>
                      )}

                      {/* Server Barcode Data */}
                      {idVerification.server_barcode_data && (
                        <Card withBorder p="sm" mb="sm">
                          <Group
                            align="center"
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggleSection('serverData')}
                          >
                            <Text fw={500} size="sm" style={{ flex: 1 }}>
                              Server Processed Data
                            </Text>
                            {expandedSections.serverData ?
                              <IconChevronUp size="1rem" /> :
                              <IconChevronDown size="1rem" />
                            }
                          </Group>
                          <Collapse in={expandedSections.serverData}>
                            <Stack gap="xs" mt="sm">
                              {formatBarcodeData(idVerification.server_barcode_data)?.map(({ key, value }) => (
                                <Group key={key} align="apart">
                                  <Text size="sm" fw={500}>{key}:</Text>
                                  <Text size="sm" style={{ maxWidth: '60%' }} truncate>{value}</Text>
                                </Group>
                              ))}
                            </Stack>
                          </Collapse>
                        </Card>
                      )}

                      {/* Data Comparison */}
                      {idVerification.frontend_barcode_data && idVerification.server_barcode_data && (
                        <Card withBorder p="sm" bg="pink.0">
                          <Text fw={500} size="sm" mb="sm">Data Comparison</Text>
                          <Stack gap="xs">
                            {Object.keys(idVerification.frontend_barcode_data).filter((key) => key !== 'id').map(key => {
                              const frontendValue = idVerification.frontend_barcode_data[key];
                              const serverValue = idVerification.server_barcode_data[key];
                              const comparison = compareDataValues(frontendValue, serverValue);

                              return (
                                <Group key={key} align="center">
                                  <Text size="sm" fw={500} style={{ minWidth: '120px' }}>
                                    {key.replace(/_/g, ' ')}:
                                  </Text>
                                  <Badge
                                    size="sm"
                                    color={comparison === 'match' ? 'green' : comparison === 'mismatch' ? 'red' : 'gray'}
                                    variant="light"
                                  >
                                    {comparison}
                                  </Badge>
                                  {comparison === 'mismatch' && (
                                    <Tooltip
                                      label={`Frontend: "${frontendValue}" vs Server: "${serverValue}"`}
                                      multiline
                                      w={300}
                                    >
                                      <IconInfoCircle size="1rem" color="orange" />
                                    </Tooltip>
                                  )}
                                </Group>
                              );
                            })}
                          </Stack>
                        </Card>
                      )}
                    </Box>
                  )}

                  {/* Discrepancies */}
                  {idVerification.discrepancies?.length > 0 && (
                    <Alert icon={<IconAlertCircle size="1rem" />} color="orange">
                      <Text fw={500} size="sm" mb="xs">Discrepancies</Text>
                      {idVerification.discrepancies.map((d, i) => (
                        <Text key={i} size="sm">- {d}</Text>
                      ))}
                    </Alert>
                  )}

                  {/* Admin Notes */}
                  {idVerification.admin_notes && (
                    <Card withBorder p="sm" bg="yellow.0">
                      <Text fw={500} size="sm" mb="xs">Admin Notes</Text>
                      <Text size="sm">{idVerification.admin_notes}</Text>
                    </Card>
                  )}

                  {/* Rejection reason on record */}
                  {idVerification.rejection_reason && (
                    <Card withBorder p="sm" bg="red.0">
                      <Text fw={500} size="sm" mb="xs">Rejection Reason</Text>
                      <Text size="sm">{idVerification.rejection_reason}</Text>
                    </Card>
                  )}

                  {/* ID Verification Admin Actions */}
                  {idVerification.status !== 'approved' && (
                    <Card withBorder p="sm" bg="gray.0">
                      <Text fw={500} size="sm" mb="sm">ID Verification Actions</Text>
                      <Stack gap="sm">
                        <Textarea
                          label="Rejection reason"
                          description="Required when rejecting or requesting resubmission"
                          placeholder="Explain what is wrong with the submitted documents..."
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.currentTarget.value)}
                          minRows={2}
                        />
                        <Group>
                          <Button
                            size="sm"
                            color="green"
                            leftSection={<IconShieldCheck size="1rem" />}
                            onClick={() => updateIdVerificationStatus('approved')}
                            loading={updating}
                          >
                            Approve ID
                          </Button>
                          <Button
                            size="sm"
                            color="red"
                            leftSection={<IconShieldX size="1rem" />}
                            onClick={() => updateIdVerificationStatus('rejected')}
                            loading={updating}
                            disabled={!rejectionReason.trim()}
                          >
                            Reject ID
                          </Button>
                          <Button
                            size="sm"
                            color="orange"
                            leftSection={<IconAlertCircle size="1rem" />}
                            onClick={() => updateIdVerificationStatus('resubmission_required')}
                            loading={updating}
                            disabled={!rejectionReason.trim()}
                          >
                            Request Resubmission
                          </Button>
                        </Group>
                      </Stack>
                    </Card>
                  )}
                </Stack>
              )}

              {!idVerificationLoading && !idVerification && !idVerificationError && (
                <Alert icon={<IconInfoCircle size="1rem" />} color="pink">
                  No ID verification has been submitted by this seller yet.
                </Alert>
              )}
            </Card>

            {/* Metrics */}
            <Card withBorder p="md">
              <Title order={4} mb="md">Performance Metrics</Title>
              <Group grow>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Total Sales</Text>
                  <Text size="lg" fw={700}>${((selectedSeller.total_sales_cents ?? 0) / 100).toFixed(2)}</Text>
                </Stack>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Total Orders</Text>
                  <Text size="lg" fw={700}>{selectedSeller.total_orders ?? 0}</Text>
                </Stack>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Average Rating</Text>
                  <Text size="lg" fw={700}>{selectedSeller.average_rating ?? 0}/5</Text>
                </Stack>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Total Reviews</Text>
                  <Text size="lg" fw={700}>{selectedSeller.total_reviews ?? 0}</Text>
                </Stack>
              </Group>
            </Card>

            {/* Admin Actions */}
            <Card withBorder p="md">
              <Title order={4} mb="md">Admin Actions</Title>
              <Stack gap="md">
                <NumberInput
                  label="Commission Rate (%)"
                  value={commissionRate}
                  onChange={setCommissionRate}
                  min={0}
                  max={50}
                  step={0.1}
                  decimalScale={1}
                />

                <Textarea
                  label="Verification Notes"
                  placeholder="Add notes about the verification process..."
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.currentTarget.value)}
                  minRows={3}
                />

                <Group grow>
                  <Button
                    color="green"
                    leftSection={<IconCheck size="1rem" />}
                    onClick={() => updateSellerStatus(selectedSeller.id, 'approved', true)}
                    loading={updating}
                    disabled={selectedSeller.seller_status === 'approved'}
                  >
                    Approve & Verify
                  </Button>
                  <Button
                    color="red"
                    leftSection={<IconX size="1rem" />}
                    onClick={() => updateSellerStatus(selectedSeller.id, 'rejected', false)}
                    loading={updating}
                  >
                    Reject
                  </Button>
                  <Button
                    color="orange"
                    onClick={() => updateSellerStatus(selectedSeller.id, 'suspended', false)}
                    loading={updating}
                  >
                    Suspend
                  </Button>
                </Group>
              </Stack>
            </Card>
          </Stack>
        )}
      </Modal>

      {/* Full-size Image Modal */}
      <Modal
        opened={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        title={selectedImage?.title}
        size="xl"
        centered
      >
        {selectedImage && (
          <div style={{ textAlign: 'center' }}>
            <Image
              src={selectedImage.url}
              alt={selectedImage.title}
              fit="contain"
              style={{ maxHeight: '70vh' }}
            />
            <Group justify="center" mt="md">
              <Button
                variant="light"
                leftSection={<IconDownload size="1rem" />}
                component="a"
                href={selectedImage.url}
                download={selectedImage.title}
              >
                Download
              </Button>
            </Group>
          </div>
        )}
      </Modal>
    </Tabs.Panel>
  );
};

export default SellerVerification;
